import { ArrowDown, ArrowRight } from "lucide-react";
import { DemoCard } from "../components/DemoCard";
import { SiteNav } from "../components/SiteNav";
import { demoRegistry } from "../data/demoRegistry";
import { useI18n } from "../i18n/i18n";
import { homeMessages } from "../i18n/messages";
import { usePageMeta } from "../lib/meta";

const introductionCapabilities = {
  en: ["Business websites", "Bookings", "E-commerce", "Customer portals", "Custom web apps"],
  it: ["Siti aziendali", "Prenotazioni", "E-commerce", "Portali clienti", "Web app su misura"],
};

const advancedCapabilities = {
  en: ["Web apps", "Customer portals", "Bookings", "E-commerce", "Custom functionality"],
  it: ["Web app", "Portali clienti", "Prenotazioni", "E-commerce", "Funzionalità su misura"],
};

export function DemoCatalogue() {
  const { language, text } = useI18n();
  const isItalian = language === "it";
  const websites = demoRegistry.filter((demo) => demo.category === "websites");
  
  const title = isItalian 
    ? "Siti Web & Applicazioni Web su Misura — Mattia D’Elia" 
    : "Custom Websites & Web Applications — Mattia D’Elia";
  const description = isItalian
    ? "Siti professionali, e-commerce, prenotazioni, portali clienti e applicazioni web sviluppate intorno alla tua attività."
    : "Professional websites, e-commerce, booking systems, customer portals and custom web applications built around your business.";
  
  usePageMeta(
    title,
    description,
    "/websites",
    "/og-websites-v3.png",
    "Mattia D’Elia — websites built around your business.",
  );

  return (
    <div className="catalogue-page">
      <SiteNav />
      <main className="catalogue-main" id="main">
        {/* Sezione Unificata Hero & Introduction */}
        <section className="catalogue-hero" aria-labelledby="catalogue-title">
          <div className="catalogue-hero-copy">
            <p className="home-eyebrow">
              {isItalian ? "Siti Web e Concepts" : "Websites & Concepts"}
            </p>
            <h1 id="catalogue-title">
              {isItalian ? "Siti web progettati intorno alla tua attività." : "Websites built around your business."}
            </h1>
            <p>
              {isItalian
                ? "Dai siti aziendali alle piattaforme e-commerce, prenotazioni e portali clienti: creo ogni progetto su misura per la tua identità e i tuoi obiettivi."
                : "From corporate sites to e-commerce, booking systems, and customer portals, I design every project around your brand, audience, and goals."}
            </p>
            <p>
              {isItalian
                ? "Le demo sottostanti sono esempi pratici di struttura e funzionalità, non template predefiniti. Ogni progetto reale parte da zero, basandosi sulle tue reali esigenze."
                : "The demos below showcase practical structures and interactions — not rigid templates. Every real-world project is built from scratch for your specific business needs."}
            </p>

            <ul className="capability-strip" aria-label={isItalian ? "Tipi di progetti web" : "Web project types"}>
              {introductionCapabilities[language].map((capability) => (
                <li key={capability}>{capability}</li>
              ))}
            </ul>

            <a className="home-button light" href="#concepts">
              {isItalian ? "Esplora le demo" : "Explore the demos"}
              <ArrowDown size={18} />
            </a>
          </div>
        </section>

        {/* Griglia Demo */}
        <section className="catalogue-group" id="concepts" aria-labelledby="concepts-title">
          <header className="catalogue-group-heading">
            <div>
              <h2 id="concepts-title">{isItalian ? "Concept per attività rivolte ai clienti." : "Concepts for customer-facing businesses."}</h2>
            </div>
          </header>
          <div className="catalogue-grid">
            {websites.map((demo) => (
              <DemoCard demo={demo} key={demo.id} />
            ))}
          </div>
        </section>

        {/* Sezione Funzionalità Avanzate */}
        <section className="catalogue-advanced" aria-labelledby="catalogue-advanced-title">
          <p className="home-eyebrow">{isItalian ? "Oltre un normale sito" : "Beyond a standard website"}</p>
          <h2 id="catalogue-advanced-title">{isItalian ? "A volte un sito diventa software." : "Sometimes a website becomes software."}</h2>
          <p>
            {isItalian
              ? "Se un progetto richiede account, dati, prenotazioni, integrazioni, workflow o logiche su misura, posso sviluppare anche quelle — dai portali clienti ad applicazioni web complete."
              : "If a project needs accounts, data, bookings, integrations, custom workflows or application logic, I can build that too — from customer portals to complete custom web applications."}
          </p>
          <ul className="capability-strip capability-strip-dark" aria-label={isItalian ? "Funzionalità avanzate" : "Advanced functionality"}>
            {advancedCapabilities[language].map((capability) => (
              <li key={capability}>{capability}</li>
            ))}
          </ul>
          <div className="catalogue-advanced-actions">
            <a className="home-button light" href="/#contact">
              {isItalian ? "Parliamo del tuo progetto" : "Discuss your project"}
              <ArrowRight size={18} />
            </a>
            <a className="catalogue-cross-link" href="/automations">
              {isItalian ? "Cerchi un sistema interno? Scopri Sistemi & Automazioni" : "Looking for an internal system? Explore Systems & Automation"}
              <ArrowRight size={17} />
            </a>
          </div>
        </section>

        
      </main>

      <footer className="home-footer">
        <span>© {new Date().getFullYear()} Mattia D’Elia</span>
        <span>{text(homeMessages.vat)}</span>
      </footer>
    </div>
  );
}
